import { Form } from "@unform/web";
import { CheckCircle, FileSignature } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { countries_list, formatter, getRegistries } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSearchParams } from "react-router-dom";
import FileInputV3 from "~/components/RegisterForm/FileInputV3";
import { AlertContext } from "~/App";
import SignaturePad from "react-signature-pad-wrapper";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { app } from "~/services/firebase";
import { pdfjs } from "react-pdf";

import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import { useSelector } from "react-redux";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
export const InputContext = createContext({});

export default function SponsorOutside({
  access = { view: true, create: true, edit: true, inactivate: false },
  handleOpened,
}) {
  const { profile } = useSelector((state) => state.auth);
  const [pageData, setPageData] = useState({
    enrollmentemergencies: [
      {
        name: "",
        relationship_type: "",
        email: "",
        phone: "",
      },
    ],
    activeMenu: null,
    loaded: false,
  });
  const [searchparams, setSearchParams] = useSearchParams();
  const [formType, setFormType] = useState("full");
  const [fullscreen, setFullscreen] = useState(true);
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(true);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const fileEnrollmentRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const id = searchparams.get("crypt");
  const { alertBox } = useContext(AlertContext);
  const signatureRef = useRef();

  const menus = [
    { order: 8, name: "sponsor-signature" },
    { order: 9, name: "transfer-agent" },
    { order: 10, name: "finished" },
  ];

  const countriesOptions = countries_list.map((country) => {
    return { value: country, label: country };
  });
  async function getDocuments(type = "") {
    const { data } = await api.get(
      `/documentsByOrigin?origin=Enrollment&type=${type}&subtype=Sponsor`
    );
    return data;
  }
  async function getPageData() {
    if (id !== "new") {
      try {
        let documents = [];
        const { data } = await api.get(`/outside/sponsors/${id}`);
        const { data: filialData } = await api.get(
          `/filials/${data.filial_id}`
        );

        documents = await getDocuments(data.students.processsubstatuses.name);

        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
        setPageData({
          ...data,
          documents,
          contracts: filialData.filialdocuments,
          loaded: true,
          activeMenu: searchparams.has("activeMenu")
            ? searchparams.get("activeMenu")
            : !data.enrollmentsponsors.find((sponsor) => sponsor.id === id)?.signature
            ? "sponsor-signature"
            : data.form_step === "transfer-agent"
            ? "sponsor-signature"
            : data.form_step,
          lastActiveMenu: menus.find((menu) => menu.name === data.form_step),
          sponsor_signature: data.enrollmentsponsors.find(
            (sponsor) => sponsor.id === id
          ).signature
            ? true
            : false,
          sponsor: data.enrollmentsponsors.find((sponsor) => sponsor.id === id),
        });
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
        console.log(err);
      }
    }
  }

  useEffect(() => {
    if (!pageData.loaded) {
      getPageData();
    }
  }, [pageData.loaded]);

  async function handleGeneralFormSubmit(data) {
    if (loading) return;
    setLoading(true);

    if (successfullyUpdated && !(pageData.activeMenu === "sponsor-signature" && !pageData.sponsor_signature)) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }

    if (pageData.activeMenu === "sponsor-signature") {
      const requiredFields = [
        { field: "name", label: "Full Name" },
        { field: "email", label: "E-mail" },
        { field: "phone", label: "Phone Number" },
        { field: "birthday", label: "Birth Date" },
        { field: "address", label: "Address" },
        { field: "zip_code", label: "Zip Code" },
        { field: "city", label: "City" },
        { field: "state", label: "State" },
        { field: "country", label: "Country" },
        { field: "birth_city", label: "Birth City" },
        { field: "birth_state", label: "Birth State" },
        { field: "birth_country", label: "Birth Country" },
      ];

      for (const { field, label } of requiredFields) {
        const value = generalForm.current.getFieldValue(field);
        if (!value || (typeof value === "string" && value.trim() === "")) {
          toast(`${label} is required.`, { type: "error", autoClose: 3000 });
          setLoading(false);
          return;
        }
      }

      const checkboxValue = generalForm.current.getFieldValue("responsible_checkbox");
      if (!checkboxValue || checkboxValue === "false") {
        toast("You must accept the responsibility checkbox.", { type: "error", autoClose: 3000 });
        setLoading(false);
        return;
      }
    }

    if (
      pageData.activeMenu === "sponsor-signature" &&
      !pageData.sponsor_signature
    ) {
      const signaturePad = signatureRef.current?.instance;
      if (!signaturePad || signaturePad.isEmpty()) {
        toast("Sponsor signature is required.", {
          type: "error",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      const { birthday } = data;

      // 1. Validate required documents
      const missingRequired = (pageData.documents || []).filter((doc) => {
        if (doc.active === false) return false;
        if (!doc.required) return false;
        const hasSaved = (pageData.enrollmentdocuments || []).some((ed) => ed.document_id === doc.id);
        const hasPending = fileEnrollmentRefs.current.some((r) => {
          return r?.documentId === doc.id && r?.hasPending();
        });
        return !hasSaved && !hasPending;
      });
      if (missingRequired.length > 0) {
        const names = missingRequired.map((d) => d.title).join(", ");
        toast(`The following required documents are missing: ${names}`, { type: "error", autoClose: 5000 });
        setLoading(false);
        return;
      }

      // 2. Capture signature data URL before any awaits (avoids stale canvas)
      const signature = signatureRef.current.toDataURL();

      // 3. Upload pending documents
      const refsWithPending = fileEnrollmentRefs.current.filter((r) => r?.hasPending());
      if (refsWithPending.length > 0) {
        const toastId = toast.loading("Files are being uploaded...");
        try {
          for (const r of refsWithPending) {
            await r.uploadPending();
          }
          toast.update(toastId, { render: "All files have been uploaded!", type: "success", autoClose: 3000, isLoading: false });
        } catch (err) {
          toast.update(toastId, { render: err?.response?.data?.error || "Upload failed.", type: "error", autoClose: 3000, isLoading: false });
          setLoading(false);
          return;
        }
      }

      // 4. Save form data (name, email, phone, address, etc.)
      try {
        await api.put(`/outside/sponsors/${id}`, {
          ...data,
          birthday: birthday ? format(birthday, "yyyy-MM-dd") : null,
          activeMenu: pageData.activeMenu,
          lastActiveMenu: menus.find(
            (menu) => menu.name === pageData.activeMenu
          ),
        });
      } catch (err) {
        toast(err?.response?.data?.error || "Failed to save sponsor data.", { type: "error", autoClose: 3000 });
        setLoading(false);
        return;
      }

      // 5. Upload signature to Firebase and save reference
      try {
        const fileUuid = uuidv4();
        const storage = getStorage(app);
        const local = "Enrollments/Signatures/" + fileUuid + ".png";
        const imageRef = ref(storage, local);
        const snapshot = await uploadString(imageRef, signature.substring(22), "base64");
        const downloadURL = await getDownloadURL(snapshot.ref);
        const { data: signatureData } = await api.post(`/enrollmentsponsorsignature`, {
          sponsor_id: id,
          files: {
            url: downloadURL,
            name: fileUuid + ".png",
            size: signature.length,
            key: fileUuid + ".png",
          },
        });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        setTimeout(() => window.location.reload(), 1000);
      } catch (err) {
        toast(err?.response?.data?.error || "Failed to upload signature.", { type: "error", autoClose: 3000 });
      }
      setLoading(false);
      return;
    }

    if (id !== "new") {
      const { birthday } = data;

      try {
        await api.put(`/outside/sponsors/${id}`, {
          ...data,
          activeMenu: pageData.activeMenu,
          lastActiveMenu: pageData.lastActiveMenu,
          birthday: birthday ? format(birthday, "yyyy-MM-dd") : null,
        });
        setPageData({ ...pageData, activeMenu: "finished", loaded: false });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
      } catch (err) {
        toast(err?.response?.data?.error || "Save failed.", { type: "error", autoClose: 3000 });
      }
      setLoading(false);
    }
  }

  function handleSignature() {
    setSuccessfullyUpdated(false);
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleDeleteDocument(id) {
    // const { file } = pageData.staffdocuments.find(staffdocument => staffdocument.id === id);
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/enrollmentdocuments/${id}`);
              toast("File deleted!", { autoClose: 1000 });
              setPageData({
                ...pageData,
                enrollmentdocuments: pageData.enrollmentdocuments.filter(
                  (enrollmentdocument) => enrollmentdocument.id !== id
                ),
              });
            } catch (err) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  function handleClearSignature() {
    const signaturePad = signatureRef.current;

    if (signaturePad) {
      signaturePad.instance.clear();
      setSuccessfullyUpdated(true);
    }
  }

  function handleInactivate() {}

  function handleOutsideMail() {}

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {!sent && pageData.loaded ? (
        <div className="flex h-full flex-col items-start justify-between gap-4 md:flex-row">
          <div className="flex flex-row items-center justify-between text-xs w-32 gap-4 md:flex-col">
            <RegisterFormMenu
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: "sponsor-signature" })
              }
              activeMenu={pageData.activeMenu}
              name="sponsor-signature"
            >
              <FileSignature size={22} /> Sponsor's Signature
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start min-w-0">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate,
                handleOutsideMail,
                canceled: pageData.canceled_at,
              }}
            >
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll overflow-x-hidden h-full w-full">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full max-w-full"
                >
                  <FormHeader
                    saveText="Save & Continue"
                    outside
                    loading={loading}
                    access={access}
                    title={
                      pageData.students.name +
                      " " +
                      pageData.students.last_name +
                      " - Enrollment Process"
                    }
                    registry={registry}
                    InputContext={InputContext}
                  />
                  {pageData.loaded ? (
                    <>
                      {pageData.activeMenu === "sponsor-signature" ? (
                        <InputLineGroup
                          title="Sponsor Signature"
                          activeMenu={
                            pageData.activeMenu === "sponsor-signature"
                          }
                        >
                          <InputLine mobileForm title="Sponsor Information">
                            <Input
                              type="text"
                              name="name"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Full Name"
                              defaultValue={pageData.sponsor.name}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="email"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="E-mail"
                              defaultValue={pageData.sponsor.email}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              name="phone"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Phone Number"
                              value={pageData.sponsor.phone}
                              InputContext={InputContext}
                            />
                            <DatePicker
                              name="birthday"
                              required
                              grow
                              readOnly={!profile && pageData?.sponsor_signature}
                              title="Birth Date"
                              defaultValue={
                                pageData?.sponsor.birthday
                                  ? parseISO(pageData?.sponsor.birthday)
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              onKeyDown={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                              onChangeRaw={(e) => e.preventDefault()}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine mobileForm title="Legal Entity (If a bank statement is in the name of a company)">
                            <Input
                              type="text"
                              name="legal_entity_name"
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Name of the company"
                              defaultValue={pageData.sponsor.legal_entity_name}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="legal_entity_email"
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="E-mail"
                              defaultValue={pageData.sponsor.legal_entity_email}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              name="legal_entity_phone"
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Phone Number"
                              value={pageData.sponsor.legal_entity_phone}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="legal_entity_address"
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Address"
                              defaultValue={
                                pageData.sponsor.legal_entity_address
                              }
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="legal_entity_position"
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Position of the person who is signing this affidavit."
                              defaultValue={
                                pageData.sponsor.legal_entity_position
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine mobileForm title="Address Information">
                            <Input
                              type="text"
                              name="address"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Address"
                              defaultValue={pageData.sponsor.address}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="zip_code"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Zip Code"
                              defaultValue={pageData.sponsor.zip_code}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine mobileForm>
                            <Input
                              type="text"
                              name="city"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="City"
                              defaultValue={pageData.sponsor.city}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="state"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="State"
                              defaultValue={pageData.sponsor.state}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="country"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Country"
                              isSearchable
                              defaultValue={countriesOptions.find(
                                (country) =>
                                  country.value === pageData.sponsor.country
                              )}
                              options={countriesOptions}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine mobileForm title="Birth Place Information">
                            <Input
                              type="text"
                              name="birth_city"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="City"
                              defaultValue={pageData.sponsor.birth_city}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="birth_state"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="State"
                              defaultValue={pageData.sponsor.birth_state}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="birth_country"
                              required
                              grow
                              readOnly={!profile && pageData.sponsor_signature}
                              title="Country"
                              isSearchable
                              defaultValue={countriesOptions.find(
                                (country) =>
                                  country.value ===
                                  pageData.sponsor.birth_country
                              )}
                              options={countriesOptions}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <div className="w-full px-2 md:px-4 py-3 flex flex-col gap-3 text-sm">
                            <h1 className="w-full border-b pb-2 font-bold">Proof of financial support</h1>
                            <p><strong>Name of the student:</strong>{" "}{`${pageData.students.name} ${pageData.students.last_name || ""}`.trim()}</p>
                            <p><strong>Country of citizen:</strong>{" "}{pageData.students.citizen_country}</p>
                            <p>Immigration requires that students submit documented proof of financial support. <em>MILA School</em> requires the amount indicated below:</p>
                            <p className="text-base font-semibold">
                              {pageData.has_dependents
                                ? formatter.format(
                                    (parseFloat(pageData.filial.financial_support_student_amount) +
                                      parseFloat(pageData.filial.financial_support_dependent_amount) *
                                        pageData.enrollmentdependents.length) *
                                      pageData.plan_months
                                  )
                                : formatter.format(
                                    parseFloat(pageData.filial.financial_support_student_amount) * pageData.plan_months
                                  )}{" "}
                              (Estimate)
                            </p>
                          </div>
                          <InputLine mobileForm>
                            {pageData.sponsor.responsible_checkbox ? (
                              <CheckboxInput
                                grow
                                name="responsible_checkbox"
                                defaultValue={true}
                                required
                                readOnly={
                                  !profile && pageData.sponsor_signature
                                }
                                title={`I will be responsible for student's financial and personal support for the durantion of hir/her studies at MILA.`}
                                InputContext={InputContext}
                              />
                            ) : (
                              <CheckboxInput
                                grow
                                name="responsible_checkbox"
                                required
                                title={`I will be responsible for student's financial and personal support for the durantion of hir/her studies at MILA.`}
                                InputContext={InputContext}
                              />
                            )}
                          </InputLine>
                          {pageData.documents &&
                            pageData.documents.length > 0 &&
                            [...pageData.documents]
                              .filter((doc) => doc.active !== false)
                              .sort((a, b) => {
                                const numA = parseInt(a.title);
                                const numB = parseInt(b.title);
                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                if (!isNaN(numA)) return -1;
                                if (!isNaN(numB)) return 1;
                                return 0;
                              })
                              .map((document, index) => (
                                <FileInputV3
                                  key={index}
                                  ref={(el) => (fileEnrollmentRefs.current[index] = el)}
                                  document={document}
                                  enrollmentdocuments={pageData.enrollmentdocuments}
                                  enrollmentId={pageData.id}
                                  sponsorId={id}
                                  isEditable={!pageData.sponsor_signature}
                                  onDelete={handleDeleteDocument}
                                  onUploaded={() => setPageData((prev) => ({ ...prev, loaded: false }))}
                                  onPendingChange={() => setSuccessfullyUpdated(false)}
                                />
                              ))}
                          {pageData.sponsor_signature ? (
                            <InputLine mobileForm title="Sponsor Signature">
                              <div className="flex w-full md:w-auto flex-col items-start justify-start min-w-0">
                                <img
                                  src={pageData.sponsor.sponsorsignature.url}
                                  className="border w-full md:w-96"
                                />
                                <p className="text-xs p-2 border border-t-0 rounded-b-md bg-slate-100 w-full md:w-96">
                                  Signed:{" "}
                                  {format(
                                    parseISO(
                                      pageData.sponsor.sponsorsignature
                                        .created_at
                                    ),
                                    "MMM do, yyyy"
                                  )}{" "}
                                  at{" "}
                                  {format(
                                    parseISO(
                                      pageData.sponsor.sponsorsignature
                                        .created_at
                                    ),
                                    "HH:mm"
                                  )}
                                </p>
                              </div>
                            </InputLine>
                          ) : (
                            <InputLine
                              mobileForm
                              title={
                                <span className="flex items-center gap-1">
                                  Sponsor Signature
                                  <span className="text-red-600">*</span>
                                </span>
                              }
                            >
                              <div className="flex w-full md:w-auto flex-col items-start justify-start min-w-0">
                                <div
                                  onClick={handleSignature}
                                  className="w-full md:w-auto border rounded-sm"
                                  style={{ height: window.innerWidth < 768 ? 160 : 240 }}
                                >
                                  <SignaturePad
                                    redrawOnResize
                                    ref={signatureRef}
                                    width={window.innerWidth < 768 ? undefined : 600}
                                    height={window.innerWidth < 768 ? 160 : 240}
                                    options={{
                                      backgroundColor: "#FFF",
                                      penColor: "#111",
                                    }}
                                  />
                                </div>
                                <div className="flex flex-row items-center justify-start gap-2">
                                  <button
                                    type="button"
                                    onClick={handleClearSignature}
                                    className="bg-primary text-white rounded-md my-2 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
                                  >
                                    Clear Signature
                                  </button>
                                </div>
                              </div>
                            </InputLine>
                          )}
                        </InputLineGroup>
                      ) : pageData.sponsor_signature ? (
                        <div className="flex h-full flex-row items-center justify-center text-center gap-4">
                          <CheckCircle size={32} color="#00b361" />
                          Thank you!
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
